import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2, Mic2, Plus, Star } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import ReplicateBanner from "@/components/myvoices/ReplicateBanner";
import NewVoiceDialog from "@/components/myvoices/NewVoiceDialog";
import { useVoiceStatus } from "@/components/myvoices/useVoice";
import { listVoices, mins } from "@/lib/myVoicesApi";
import SingUploadSection from "@/components/voicesongs/SingUploadSection";
import SunoVoicesSection from "@/components/sunovoice/SunoVoicesSection";

const STEPS = [
  ["Sign", "Confirm it's your own voice — a consent record is kept with the voice."],
  ["Add singing", "10–30 minutes of you a cappella. Upload, or reuse Enhanced vocals and studio takes."],
  ["Train", "Runs on your Replicate account. Each run is a new version."],
  ["Preview & choose", "Hear 25 seconds sung in your voice, then pick the version the studio uses."],
];

function VoiceCard({ v }) {
  return (
    <Link to={`/create/my-voices/${v.id}`} data-testid={`voice-card-${v.id}`}
      className="group flex flex-col rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-cyan-300/30">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-cyan-300 to-emerald-400 text-black"><Mic2 className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[16px] font-semibold">{v.name}</span>
          <span className="block truncate text-[12px] text-white/45">{v.notes || "Private singing voice"}</span>
        </span>
        {v.selected ? <Star data-testid={`voice-card-selected-${v.id}`} className="h-4 w-4 fill-cyan-300 text-cyan-300" /> : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 text-[12px]">
        <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{mins(v.singing_sec)} of singing</span>
        <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{v.clip_count} recordings</span>
        {v.training ? <span className="flex items-center gap-1 rounded-full bg-amber-300/10 px-2.5 py-1 text-amber-100"><Loader2 className="h-3 w-3 animate-spin" /> Training</span> : null}
        {v.active_version?.status === "ready" ? <span className="rounded-full bg-cyan-300/15 px-2.5 py-1 text-cyan-100">Version {v.active_version.n} ready</span> : null}
      </div>
      <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 group-hover:text-white">Open <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
    </Link>
  );
}

/** `/create/my-voices` — your private singing voices. */
export default function MyVoicesPage() {
  const status = useVoiceStatus();
  const [voices, setVoices] = useState(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => { listVoices().then(setVoices).catch(() => setVoices([])); }, []);
  return (
    <CreatorShell testId="my-voices-page" back="/create" title="My Voices" subtitle="Your private singing voice — learned only from your recordings, kept in your studio.">
      <div className="space-y-6">
        <SunoVoicesSection />
        <div data-testid="older-voices-heading" className="border-t border-white/[0.06] pt-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45">Older voices</p>
          <h2 className="mt-2 font-display text-base font-semibold text-white/80 md:text-lg">Voices trained on Replicate</h2>
        </div>
        <ReplicateBanner status={status} />
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([t, b], i) => (
            <li key={t} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
              <span className="font-mono text-[11px] text-cyan-300">0{i + 1}</span>
              <div className="mt-1 text-[14px] font-semibold">{t}</div>
              <p className="mt-1 text-[12px] leading-relaxed text-white/50">{b}</p>
            </li>
          ))}
        </ol>
        {voices === null ? <Loader2 className="h-6 w-6 animate-spin text-white/40" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="voice-grid">
            {voices.map((v) => <VoiceCard key={v.id} v={v} />)}
            <button type="button" data-testid="voice-new" onClick={() => setCreating(true)}
              className="flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-white/12 text-white/60 transition-colors hover:border-cyan-300/40 hover:text-white">
              <Plus className="h-6 w-6" /><span className="font-display text-[15px] font-semibold">{voices.length ? "New voice" : "Create your voice"}</span>
            </button>
          </div>
        )}
        <SingUploadSection />
      </div>
      <NewVoiceDialog open={creating} onClose={() => setCreating(false)} consentText={status?.consent_text} />
    </CreatorShell>
  );
}
